import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import apiClient from "../api/apiClient";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import VentePaymentModal from "../gestion-vente/components/VentePaymentModal.tsx";
import RowActionsMenu from "../components/RowActionsMenu";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const APPOINTMENT_PERMISSIONS = {
  VIEW: "Gestion Commerciale.RendezVous.view",
  CREATE: "Gestion Commerciale.RendezVous.create",
  EDIT: "Gestion Commerciale.RendezVous.edit",
  DELETE: "Gestion Commerciale.RendezVous.delete",
};

const SALES_CREATE_PERMISSION = "Gestion Commerciale.Ventes.Ventes.create";
const POS_USE_PERMISSION = "Gestion Commerciale.POS.use";
const INCOMING_PAYMENT_CREATE_PERMISSION =
  "Gestion Commerciale.Ventes.PaiementsEntrants.create";

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Programmé" },
  { value: "confirmed", label: "Confirmé" },
  { value: "in_progress", label: "En cours" },
  { value: "completed", label: "Terminé" },
  { value: "cancelled", label: "Annulé" },
  { value: "no_show", label: "Absent" },
];

const STATUS_COLOR = {
  scheduled: "blue",
  confirmed: "cyan",
  in_progress: "orange",
  completed: "green",
  cancelled: "red",
  no_show: "volcano",
};

const formatMoney = (value) =>
  `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))} CFA`;

const roundToNextHalfHour = () => {
  const now = dayjs();
  const next = now.minute() < 30 ? now.minute(30) : now.add(1, "hour").minute(0);
  return next.second(0);
};

const toCompanyId = (selectedCompany) =>
  selectedCompany?.id ?? (selectedCompany ? Number(selectedCompany) : null);

const toWarehouseId = (selectedWarehouse) =>
  selectedWarehouse?.id ?? (selectedWarehouse ? Number(selectedWarehouse) : null);

const GestionRendezVous = () => {
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const [loading, setLoading] = useState(false);
  const [refsLoading, setRefsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [convertingAppointmentId, setConvertingAppointmentId] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    warehouse: "all",
    dateRange: [dayjs().startOf("day"), dayjs().add(7, "day").endOf("day")],
  });

  const [form] = Form.useForm();
  const { user } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();

  const companyId = useMemo(() => toCompanyId(selectedCompany), [selectedCompany]);
  const selectedWarehouseId = useMemo(
    () => toWarehouseId(selectedWarehouse),
    [selectedWarehouse]
  );

  const hasPermission = useCallback(
    (permissionKey) => user?.permissions?.includes(permissionKey) || false,
    [user]
  );

  const canView = hasPermission(APPOINTMENT_PERMISSIONS.VIEW);
  const canCreate = hasPermission(APPOINTMENT_PERMISSIONS.CREATE);
  const canEdit = hasPermission(APPOINTMENT_PERMISSIONS.EDIT);
  const canDelete = hasPermission(APPOINTMENT_PERMISSIONS.DELETE);
  const canCreateSalesOrder = hasPermission(SALES_CREATE_PERMISSION);
  const canUsePos = hasPermission(POS_USE_PERMISSION);
  const canConvertToSale = canCreateSalesOrder || canUsePos;
  const canCreateIncomingPayment = hasPermission(
    INCOMING_PAYMENT_CREATE_PERMISSION
  );

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      warehouse: selectedWarehouseId ? String(selectedWarehouseId) : "all",
    }));
  }, [selectedWarehouseId, companyId]);

  const servicesById = useMemo(() => {
    const map = new Map();
    services.forEach((service) => map.set(Number(service.id), service));
    return map;
  }, [services]);

  const customersById = useMemo(() => {
    const map = new Map();
    customers.forEach((customer) => map.set(Number(customer.id), customer));
    return map;
  }, [customers]);

  const getConvertedOrderAmounts = useCallback((appointment) => {
    const totalRaw = Number(
      appointment?.converted_order_total ?? appointment?.total_price ?? 0
    );
    const paidRaw = Number(appointment?.converted_order_paid_amount ?? 0);
    const dueRaw = Number(appointment?.converted_order_due_amount);

    const total = Number.isFinite(totalRaw) && totalRaw >= 0 ? totalRaw : 0;
    const paid = Number.isFinite(paidRaw) && paidRaw >= 0 ? paidRaw : 0;
    const derivedDue = Math.max(0, total - paid);
    const due =
      Number.isFinite(dueRaw) && dueRaw >= 0
        ? Math.min(dueRaw, Math.max(total, dueRaw))
        : derivedDue;

    return { total, paid, due };
  }, []);

  const fetchReferences = useCallback(async () => {
    if (!companyId || !canView) {
      setServices([]);
      setCustomers([]);
      setStaffMembers([]);
      setWarehouses([]);
      return;
    }

    setRefsLoading(true);
    try {
      const [servicesRes, customersRes, staffRes, warehousesRes] = await Promise.all([
        apiClient.get("/api/services", {
          params: {
            company_id: companyId,
            status: "active",
            page: 1,
            limit: 500,
          },
        }),
        apiClient.get("/api/users/customers", {
          params: {
            companyId,
            ...(selectedWarehouseId ? { warehouseId: selectedWarehouseId } : {}),
          },
        }),
        apiClient.get("/api/users", {
          params: {
            companyId,
            user_type: "staff_members",
            page: 1,
            limit: 500,
          },
        }),
        apiClient.get("/api/warehouses", {
          params: { company_id: companyId, page: 1, limit: 500 },
        }),
      ]);

      const rawCustomers = Array.isArray(customersRes.data) ? customersRes.data : [];
      const { deduplicateEntities, countDuplicates } = await import(
        "../utils/entityUtils"
      );
      const uniqueCustomers = deduplicateEntities(rawCustomers);
      const customerStats = countDuplicates(rawCustomers);
      if (customerStats.duplicates > 0) {
        console.warn(
          `[RENDEZVOUS] Doublons clients detectes et corriges: ${customerStats.duplicates} sur ${customerStats.total} entrees`
        );
      }

      setServices(servicesRes.data?.services || []);
      setCustomers(uniqueCustomers);
      setStaffMembers(staffRes.data?.users || []);
      setWarehouses(warehousesRes.data?.warehouses || []);
    } catch (error) {
      console.error("[RENDEZVOUS] Error loading references:", error);
      message.error("Impossible de charger les donnees de reference.");
    } finally {
      setRefsLoading(false);
    }
  }, [companyId, canView, selectedWarehouseId]);

  const fetchAppointments = useCallback(async () => {
    if (!companyId || !canView) {
      setAppointments([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        company_id: companyId,
        page: 1,
        limit: 500,
      };

      if (filters.status && filters.status !== "all") {
        params.status = filters.status;
      }

      if (filters.search.trim()) {
        params.search = filters.search.trim();
      }
      if (filters.dateRange?.[0]) {
        params.date_from = filters.dateRange[0].toISOString();
      }
      if (filters.dateRange?.[1]) {
        params.date_to = filters.dateRange[1].toISOString();
      }

      if (filters.warehouse === "__global__") {
        params.warehouse_id = "null";
      } else if (filters.warehouse !== "all") {
        params.warehouse_id = filters.warehouse;
      }

      const response = await apiClient.get("/api/service-appointments", { params });
      setAppointments(response.data?.appointments || []);
    } catch (error) {
      console.error("[RENDEZVOUS] Error loading appointments:", error);
      message.error("Impossible de charger les rendez-vous.");
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, canView, filters]);

  useEffect(() => {
    fetchReferences();
  }, [fetchReferences]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const openCreateModal = () => {
    if (!companyId) {
      message.warning("Selectionnez d'abord une entreprise.");
      return;
    }

    setEditingAppointment(null);
    form.resetFields();
    form.setFieldsValue({
      service_id: undefined,
      client_id: undefined,
      customer_name: "",
      customer_phone: "",
      staff_user_id: undefined,
      warehouse_id: selectedWarehouseId ? String(selectedWarehouseId) : "__global__",
      starts_at: roundToNextHalfHour(),
      duration_minutes: 30,
      total_price: 0,
      status: "scheduled",
      notes: "",
    });
    setModalOpen(true);
  };

  const openEditModal = (appointment) => {
    setEditingAppointment(appointment);
    form.resetFields();
    form.setFieldsValue({
      service_id: appointment.service_id ? Number(appointment.service_id) : undefined,
      client_id: appointment.client_id ? Number(appointment.client_id) : undefined,
      customer_name: appointment.customer_name || "",
      customer_phone: appointment.customer_phone || "",
      staff_user_id: appointment.staff_user_id
        ? Number(appointment.staff_user_id)
        : undefined,
      warehouse_id: appointment.warehouse_id
        ? String(appointment.warehouse_id)
        : "__global__",
      starts_at: appointment.starts_at ? dayjs(appointment.starts_at) : roundToNextHalfHour(),
      duration_minutes: Number(appointment.duration_minutes || 30),
      total_price: Number(appointment.total_price || 0),
      status: appointment.status || "scheduled",
      notes: appointment.notes || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingAppointment(null);
    form.resetFields();
  };

  const handleServiceChange = (serviceId) => {
    const service = servicesById.get(Number(serviceId));
    if (!service) return;

    const updates = {};
    if (service.duration_minutes) {
      updates.duration_minutes = Number(service.duration_minutes);
    }
    if (service.base_price !== undefined && service.base_price !== null) {
      updates.total_price = Number(service.base_price);
    }
    if (service.warehouse_id) {
      updates.warehouse_id = String(service.warehouse_id);
    }
    form.setFieldsValue(updates);
  };

  const handleCustomerChange = (clientId) => {
    const customer = customersById.get(Number(clientId));
    if (!customer) return;
    form.setFieldsValue({
      customer_name: customer.name || "",
      customer_phone: customer.phone || "",
    });
  };

  const handleSubmit = async (values) => {
    if (!companyId) {
      message.error("Aucune entreprise selectionnee.");
      return;
    }

    const payload = {
      company_id: companyId,
      service_id: Number(values.service_id),
      client_id: values.client_id ? Number(values.client_id) : null,
      staff_user_id: values.staff_user_id ? Number(values.staff_user_id) : null,
      warehouse_id:
        values.warehouse_id && values.warehouse_id !== "__global__"
          ? Number(values.warehouse_id)
          : null,
      customer_name: values.customer_name?.trim() || null,
      customer_phone: values.customer_phone?.trim() || null,
      starts_at: values.starts_at?.toISOString(),
      duration_minutes: values.duration_minutes,
      status: values.status,
      total_price: values.total_price,
      notes: values.notes?.trim() || null,
    };

    setSubmitting(true);
    try {
      if (editingAppointment) {
        await apiClient.put(
          `/api/service-appointments/${editingAppointment.id}`,
          payload
        );
        message.success("Rendez-vous mis a jour avec succes.");
      } else {
        await apiClient.post("/api/service-appointments", payload);
        message.success("Rendez-vous cree avec succes.");
      }
      closeModal();
      fetchAppointments();
    } catch (error) {
      const backendMessage = error.response?.data?.error;
      message.error(backendMessage || "Erreur lors de la sauvegarde du rendez-vous.");
      console.error("[RENDEZVOUS] Error saving appointment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (appointment) => {
    Modal.confirm({
      title: "Supprimer ce rendez-vous ?",
      content: "Cette action est definitive.",
      okText: "Supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      onOk: async () => {
        try {
          await apiClient.delete(`/api/service-appointments/${appointment.id}`);
          message.success("Rendez-vous supprime avec succes.");
          fetchAppointments();
        } catch (error) {
          const backendMessage = error.response?.data?.error;
          message.error(backendMessage || "Erreur lors de la suppression.");
          console.error("[RENDEZVOUS] Error deleting appointment:", error);
        }
      },
    });
  };

  const handleConvertToSale = async (appointment) => {
    if (!companyId) {
      message.error("Aucune entreprise selectionnee.");
      return;
    }
    if (!canConvertToSale || !canEdit) {
      message.error(
        "Permissions insuffisantes pour convertir ce rendez-vous en vente."
      );
      return;
    }

    const appointmentStatus = String(appointment.status || "").trim().toLowerCase();
    if (appointmentStatus === "cancelled" || appointmentStatus === "no_show") {
      message.error("Un rendez-vous annule ou absent ne peut pas etre converti.");
      return;
    }

    if (appointment.converted_order_id) {
      message.info("Ce rendez-vous est deja converti en commande.");
      return;
    }

    const service = servicesById.get(Number(appointment.service_id));
    const serviceId = Number(appointment.service_id || service?.id || 0);
    if (!serviceId || !service) {
      message.error("Le service de ce rendez-vous est introuvable.");
      return;
    }

    const clientId = appointment.client_id ? Number(appointment.client_id) : null;
    if (!clientId) {
      message.error(
        "Le rendez-vous doit etre rattache a un client avant conversion."
      );
      return;
    }

    const warehouseId = appointment.warehouse_id
      ? Number(appointment.warehouse_id)
      : service?.warehouse_id
      ? Number(service.warehouse_id)
      : selectedWarehouseId
      ? Number(selectedWarehouseId)
      : null;
    if (!warehouseId) {
      message.error(
        "Aucun établissement n'est defini pour ce rendez-vous. Renseignez l'établissement puis reessayez."
      );
      return;
    }

    const unitPriceRaw = Number(appointment.total_price || service?.base_price || 0);
    const unitPrice =
      Number.isFinite(unitPriceRaw) && unitPriceRaw >= 0 ? unitPriceRaw : 0;
    const orderType = canCreateSalesOrder ? "sales" : "POS";

    const payload = {
      company_id: companyId,
      invoice_type: "standard",
      order_type: orderType,
      order_date: new Date().toISOString(),
      warehouse_id: warehouseId,
      user_id: clientId,
      staff_user_id: appointment.staff_user_id
        ? Number(appointment.staff_user_id)
        : null,
      tax_id: null,
      tax_rate: 0,
      tax_amount: 0,
      discount: 0,
      manual_discount: 0,
      loyalty_discount: 0,
      loyalty_discount_rate: 0,
      loyalty_discount_applied: 0,
      shipping: 0,
      subtotal: unitPrice,
      total: unitPrice,
      paid_amount: 0,
      due_amount: unitPrice,
      payment_status: "Non paye",
      order_status: "delivered",
      total_items: 1,
      total_quantity: 1,
      notes: `Conversion rendez-vous #${appointment.id}`,
      terms_condition: "",
      items: [
        {
          item_type: "service",
          product_id: null,
          service_id: serviceId,
          item_name: service.name || appointment.service_name,
          quantity: 1,
          unit_price: unitPrice,
          single_unit_price: unitPrice,
          unit_id: null,
          tax_id: null,
          tax_rate: 0,
          tax_type: null,
          total_tax: 0,
          discount_rate: 0,
          total_discount: 0,
          subtotal: unitPrice,
        },
      ],
    };

    setConvertingAppointmentId(Number(appointment.id));
    try {
      const orderResponse = await apiClient.post("/api/orders", payload);
      const orderId = Number(
        orderResponse.data?.orderId || orderResponse.data?.id || 0
      );
      if (!orderId) {
        throw new Error("Commande creee mais identifiant introuvable.");
      }

      await apiClient.post(`/api/service-appointments/${appointment.id}/link-order`, {
        order_id: orderId,
      });

      const invoiceNumber = orderResponse.data?.invoice_number;
      message.success(
        invoiceNumber
          ? `Rendez-vous converti en vente (${invoiceNumber}).`
          : "Rendez-vous converti en vente avec succes."
      );
      fetchAppointments();
    } catch (error) {
      const backendMessage =
        error.response?.data?.error || error.response?.data?.message;
      message.error(
        backendMessage || "Erreur lors de la conversion du rendez-vous."
      );
      console.error("[RENDEZVOUS] Error converting appointment:", error);
    } finally {
      setConvertingAppointmentId(null);
    }
  };

  const openPaymentModal = (appointment) => {
    if (!canCreateIncomingPayment) {
      message.error(
        "Vous n'avez pas la permission d'enregistrer un paiement entrant."
      );
      return;
    }

    const orderId = Number(appointment.converted_order_id || 0);
    if (!orderId) {
      message.warning("Ce rendez-vous doit d'abord etre converti en vente.");
      return;
    }

    const customerId = Number(appointment.client_id || 0);
    if (!customerId) {
      message.error("Client introuvable pour cette commande.");
      return;
    }

    const warehouseId = Number(
      appointment.converted_order_warehouse_id ||
        appointment.warehouse_id ||
        selectedWarehouseId ||
        0
    );
    if (!warehouseId) {
      message.error(
        "Établissement introuvable pour cette commande. Impossible d'encaisser."
      );
      return;
    }

    const { total, paid, due } = getConvertedOrderAmounts(appointment);
    if (due <= 0.0001) {
      message.info("Cette commande est deja totalement reglee.");
      return;
    }

    setPaymentOrder({
      id: orderId,
      invoice_number:
        appointment.converted_order_invoice_number || `#${orderId}`,
      company_id: companyId,
      warehouse_id: warehouseId,
      user_id: customerId,
      total,
      paid_amount: paid,
      due_amount: due,
      payment_status:
        appointment.converted_order_payment_status || "Partiellement paye",
    });
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentOrder(null);
  };

  const handlePaymentAdded = async () => {
    await fetchAppointments();
  };

  const serviceOptions = useMemo(
    () =>
      services.map((service) => ({
        value: Number(service.id),
        label: service.name,
      })),
    [services]
  );

  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        value: Number(customer.id),
        label: customer.name,
      })),
    [customers]
  );

  const staffOptions = useMemo(
    () =>
      staffMembers.map((staff) => ({
        value: Number(staff.id),
        label: staff.name,
      })),
    [staffMembers]
  );

  const warehouseOptions = useMemo(
    () =>
      warehouses.map((warehouse) => ({
        value: String(warehouse.id),
        label: warehouse.name,
      })),
    [warehouses]
  );

  const columns = [
    {
      title: "Date / Heure",
      key: "starts_at",
      render: (_, record) => (
        <div>
          <div>{dayjs(record.starts_at).format("DD/MM/YYYY HH:mm")}</div>
          <Text type="secondary">{`${record.duration_minutes || 0} min`}</Text>
        </div>
      ),
      sorter: (a, b) => dayjs(a.starts_at).valueOf() - dayjs(b.starts_at).valueOf(),
    },
    {
      title: "Client",
      key: "customer_name",
      render: (_, record) => (
        <div>
          <div>{record.customer_name || record.client_name_ref || "-"}</div>
          <Text type="secondary">{record.customer_phone || "-"}</Text>
        </div>
      ),
    },
    {
      title: "Service",
      dataIndex: "service_name",
      key: "service_name",
      render: (value) => value || "-",
    },
    {
      title: "Praticien",
      dataIndex: "staff_name",
      key: "staff_name",
      render: (value) => value || <Text type="secondary">Non assigne</Text>,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (value) => {
        const option = STATUS_OPTIONS.find((item) => item.value === value);
        return <Tag color={STATUS_COLOR[value] || "default"}>{option?.label || value}</Tag>;
      },
    },
    {
      title: "Vente liee",
      key: "converted_order_id",
      render: (_, record) => {
        if (!record.converted_order_id) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Tag color="green">
            {record.converted_order_invoice_number ||
              `#${record.converted_order_id}`}
          </Tag>
        );
      },
    },
    {
      title: "Paiement",
      key: "payment",
      render: (_, record) => {
        if (!record.converted_order_id) {
          return <Text type="secondary">Non facture</Text>;
        }
        const { due } = getConvertedOrderAmounts(record);
        const paymentStatus = String(
          record.converted_order_payment_status || ""
        ).trim();
        const isPaid = due <= 0.0001;
        const tagColor = isPaid
          ? "green"
          : paymentStatus.toLowerCase().includes("part")
          ? "orange"
          : "red";
        const tagLabel = isPaid
          ? "Paye"
          : paymentStatus || "Non paye";

        return (
          <div>
            <Tag color={tagColor}>{tagLabel}</Tag>
            <div>
              <Text type="secondary">Reste: {formatMoney(due)}</Text>
            </div>
          </div>
        );
      },
    },
    {
      title: "Montant",
      dataIndex: "total_price",
      key: "total_price",
      render: (value) => formatMoney(value),
    },
    {
      title: "Actions",
      key: "actions",
      width: 72,
      align: "center",
      render: (_, record) => {
        const actionItems = [];
        const isConverting =
          Number(convertingAppointmentId) === Number(record.id);

        if (canEdit && canConvertToSale && !record.converted_order_id) {
          actionItems.push({
            key: "convert",
            icon: <ShoppingCartOutlined />,
            label: isConverting ? "Conversion en cours…" : "Convertir en vente",
            disabled: isConverting,
            onClick: () => handleConvertToSale(record),
          });
        }

        if (
          canCreateIncomingPayment &&
          record.converted_order_id &&
          getConvertedOrderAmounts(record).due > 0.0001
        ) {
          actionItems.push({
            key: "payment",
            icon: <DollarOutlined />,
            label: "Encaisser",
            onClick: () => openPaymentModal(record),
          });
        }

        if (canEdit) {
          actionItems.push({
            key: "edit",
            icon: <EditOutlined />,
            label: "Modifier",
            onClick: () => openEditModal(record),
          });
        }

        if (canDelete) {
          if (actionItems.length > 0) actionItems.push({ type: "divider" });
          actionItems.push({
            key: "delete",
            icon: <DeleteOutlined />,
            label: "Supprimer",
            danger: true,
            onClick: () => handleDelete(record),
          });
        }

        return (
          <RowActionsMenu
            items={actionItems}
            loading={isConverting}
            ariaLabel={`Afficher les actions du rendez-vous ${record.id}`}
          />
        );
      },
    },
  ];

  if (!canView) {
    return (
      <div className="p-sm md:p-md lg:p-lg min-h-screen">
        <Alert
          type="warning"
          showIcon
          message="Acces refuse"
          description="Vous n'avez pas la permission d'afficher les rendez-vous."
        />
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="p-sm md:p-md lg:p-lg min-h-screen">
        <Alert
          type="warning"
          showIcon
          message="Aucune entreprise selectionnee"
          description="Selectionnez une entreprise pour gerer les rendez-vous."
        />
      </div>
    );
  }

  return (
    <div className="p-sm md:p-md lg:p-lg min-h-screen">
      <div
        className="mb-lg p-md shadow rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        style={{ backgroundColor: "var(--bg-primary)" }}
      >
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold flex items-center">
            <CalendarOutlined className="mr-3" /> Gestion des Rendez-vous
          </h1>
          <p className="mt-1 text-xs sm:text-sm" style={{ color: "var(--text-secondary)" }}>
            Planifiez les prestations de votre institut et suivez les creneaux.
          </p>
        </div>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Nouveau rendez-vous
          </Button>
        )}
      </div>

      <div
        className="mb-md p-md shadow rounded-lg flex flex-col lg:flex-row gap-3"
        style={{ backgroundColor: "var(--bg-primary)" }}
      >
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Rechercher client, service ou praticien"
          value={filters.search}
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, search: event.target.value }))
          }
        />

        <Select
          value={filters.status}
          onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
          options={[
            { value: "all", label: "Tous statuts" },
            ...STATUS_OPTIONS,
          ]}
          style={{ minWidth: 190 }}
        />

        <Select
          value={filters.warehouse}
          onChange={(value) => setFilters((prev) => ({ ...prev, warehouse: value }))}
          options={[
            { value: "all", label: "Tous les établissements" },
            { value: "__global__", label: "Global entreprise" },
            ...warehouseOptions,
          ]}
          style={{ minWidth: 230 }}
          loading={refsLoading}
        />

        <RangePicker
          value={filters.dateRange}
          onChange={(range) =>
            setFilters((prev) => ({
              ...prev,
              dateRange: range && range.length === 2 ? range : [null, null],
            }))
          }
          showTime
          format="DD/MM/YYYY HH:mm"
        />
      </div>

      <div className="shadow rounded-lg" style={{ backgroundColor: "var(--bg-primary)" }}>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={appointments}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1500 }}
          locale={{ emptyText: "Aucun rendez-vous trouve pour ces filtres." }}
        />
      </div>

      <Modal
        title={editingAppointment ? "Modifier le rendez-vous" : "Nouveau rendez-vous"}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okButtonProps={{ loading: submitting }}
        okText={editingAppointment ? "Mettre a jour" : "Creer"}
        cancelText="Annuler"
        width={1000}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Form.Item
              label="Service"
              name="service_id"
              rules={[{ required: true, message: "Le service est requis." }]}
            >
              <Select
                showSearch
                options={serviceOptions}
                loading={refsLoading}
                optionFilterProp="label"
                onChange={handleServiceChange}
              />
            </Form.Item>

            <Form.Item label="Client" name="client_id">
              <Select
                showSearch
                allowClear
                options={customerOptions}
                loading={refsLoading}
                optionFilterProp="label"
                onChange={handleCustomerChange}
              />
            </Form.Item>

            <Form.Item
              label="Nom du client"
              name="customer_name"
              rules={[{ required: true, message: "Le nom du client est requis." }]}
            >
              <Input maxLength={191} />
            </Form.Item>

            <Form.Item label="Telephone du client" name="customer_phone">
              <Input maxLength={64} />
            </Form.Item>

            <Form.Item label="Praticien" name="staff_user_id">
              <Select
                showSearch
                allowClear
                options={staffOptions}
                loading={refsLoading}
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item label="Établissement" name="warehouse_id">
              <Select
                options={[
                  { value: "__global__", label: "Global entreprise" },
                  ...warehouseOptions,
                ]}
                loading={refsLoading}
              />
            </Form.Item>

            <Form.Item
              label="Debut"
              name="starts_at"
              rules={[{ required: true, message: "La date de debut est requise." }]}
            >
              <DatePicker
                showTime
                format="DD/MM/YYYY HH:mm"
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item
              label="Duree (minutes)"
              name="duration_minutes"
              rules={[
                { required: true, message: "La duree est requise." },
                {
                  type: "number",
                  min: 5,
                  message: "La duree doit etre au moins 5 minutes.",
                },
              ]}
            >
              <InputNumber min={5} max={600} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              label="Tarif total (CFA)"
              name="total_price"
              rules={[
                { required: true, message: "Le montant est requis." },
                {
                  type: "number",
                  min: 0,
                  message: "Le montant doit etre positif ou nul.",
                },
              ]}
            >
              <InputNumber min={0} precision={2} step={100} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              label="Statut"
              name="status"
              rules={[{ required: true, message: "Le statut est requis." }]}
            >
              <Select options={STATUS_OPTIONS} />
            </Form.Item>

            <Form.Item label="Notes" name="notes" className="md:col-span-2 xl:col-span-3">
              <Input.TextArea rows={3} maxLength={3000} />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <VentePaymentModal
        visible={paymentModalOpen}
        onClose={closePaymentModal}
        order={paymentOrder}
        onPaymentAdded={handlePaymentAdded}
      />
    </div>
  );
};

export default GestionRendezVous;
